/** Casco do painel: barra no alto, régua de teclas (embaixo no celular, à esquerda no
 * desktop) e a vista no meio. A URL é o estado: cada aba é uma rota, compartilhável.
 */

import { useEffect } from "react";
import { Defs } from "./Defs";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { isDark, usePrefs } from "../lib/prefs";
import { Icon, type IconName } from "./Icon";
import { Key, Seg } from "./ui";
import { useSimulation } from "../lib/simulation";
import { money } from "../lib/format";
import { useFamilies } from "../lib/queries";

const TABS: { to: string; icon: IconName; label: string }[] = [
  { to: "/hoje", icon: "gauge", label: "Hoje" },
  { to: "/fluxo", icon: "rows", label: "Fluxo" },
  { to: "/contas", icon: "cards", label: "Contas" },
  { to: "/planejado", icon: "plan", label: "Planejado" },
];

export function AppShell() {
  const { theme, setTheme, scope, setScope } = usePrefs();
  const nav = useNavigate();
  const loc = useLocation();
  const sim = useSimulation();
  const { data: familias } = useFamilies();
  // A visão conjunta só faz sentido com gente dentro; uma família de um é a visão "Meu".
  const familia = (familias ?? []).find((f) => (f.members?.length ?? 0) > 1);
  const dark = isDark(theme);
  // O servidor quer `family:<id>` e recusa "family" com 422 — o seletor antigo mandava o valor
  // sem id e a projeção da família nunca carregava. Escopo guardado que não bate com nenhuma
  // família atual (a de antes desta correção, ou uma que você deixou) volta para "Meu".
  const escopoFamilia = familia ? `family:${familia.id}` : null;
  useEffect(() => {
    if (scope === "mine" || familias === undefined) return;
    if (scope !== escopoFamilia) setScope("mine");
  }, [scope, familias, escopoFamilia, setScope]);

  return (
    <div className={`app ${sim.active ? "sim" : ""}`}>
      <Defs />
      <div className="topbar">
        <div className="brand">
          <i />
          Fluxo
        </div>
        <div className="right">
          {escopoFamilia && (
            <Seg
              label="Escopo"
              value={scope === escopoFamilia ? escopoFamilia : "mine"}
              onChange={setScope}
              options={[
                { id: "mine", label: "Meu" },
                { id: escopoFamilia, label: "Família" },
              ]}
            />
          )}
          <button
            type="button"
            role="switch"
            aria-checked={sim.active}
            onClick={() => (sim.active ? sim.discard() : sim.start())}
            className="switch topsw"
            title="Ligar ou desligar o modo simulação"
          >
            <span className="tr" />
            <span className="lbl">Simulação</span>
          </button>
          <Key
            icon={dark ? "sun" : "moon"}
            title={dark ? "Tema claro" : "Tema escuro"}
            onClick={() => setTheme(dark ? "light" : "dark")}
          />
          <Key icon="settings" title="Ajustes" onClick={() => nav("/ajustes")} />
        </div>
      </div>

      {sim.active && (
        <div className="simbar" role="status">
          <span>
            <b>Simulação</b> ·{" "}
            {sim.count === 0
              ? 'nada simulado ainda · use o + em qualquer dia, ou "Só simular" ao lançar'
              : `${sim.count} ${sim.count === 1 ? "item hipotético" : "itens hipotéticos"}${
                  sim.delta !== 0 ? ` · ${money(sim.delta)} no saldo` : ""
                }`}
          </span>
          <span className="acts">
            <Key onClick={sim.discard}>Descartar</Key>
            <Key onClick={sim.apply} disabled={sim.count === 0}>
              Aplicar
            </Key>
          </span>
        </div>
      )}

      <Outlet />

      <nav className="rail" aria-label="Principal">
        {TABS.slice(0, 2).map((t) => (
          <RailLink key={t.to} {...t} />
        ))}
        <button
          type="button"
          className="launch"
          aria-label="Lançar"
          onClick={() => nav(`/lancar?volta=${encodeURIComponent(loc.pathname)}`)}
        >
          <Icon name="plus" />
        </button>
        {TABS.slice(2).map((t) => (
          <RailLink key={t.to} {...t} />
        ))}
      </nav>
    </div>
  );
}

function RailLink({ to, icon, label }: { to: string; icon: IconName; label: string }) {
  return (
    <NavLink to={to} end>
      <Icon name={icon} />
      {label}
      <i />
    </NavLink>
  );
}
